import { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import BackButtonHandler from './BackButtonHandler'; // ✅ add this line
import AnalyticsTracker from './components/AnalyticsTracker';
import { initializeAnalytics } from './services/analyticsService';

// Pages
import Dashboard from './pages/Dashboard';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AddCruise from './pages/AddCruise';
import EditCruise from './pages/EditCruise';
import CruiseDetails from './pages/CruiseDetails';
import NotFound from './pages/NotFound';
import VerifyEmail from './pages/VerifyEmail';
import Highlights from './pages/Highlights';
import ResetPassword from './pages/ResetPassword';
import UserProfile from './pages/UserProfile';
import Help from './pages/Help';
import PostCruiseDetails from './pages/PostCruiseDetails';
import Collection from './pages/Collection';
import ShipDetails from './pages/ShipDetails';
import ShipClass from './pages/ShipClass';
import PortCleanup from './pages/PortCleanup';
import Admin from './pages/Admin';
import Rewind from './pages/Rewind';
import AdminItineraryUpload from './pages/AdminItineraryUpload';
import ShipClassNormalization from './pages/ShipClassNormalization';
import AdminAnalytics from './pages/AdminAnalytics';
import AdminDataIntegrity from './pages/AdminDataIntegrity';
import AdminShipItineraryManager from './pages/AdminShipItineraryManager';
import AdminPhotoManager from './pages/AdminPhotoManager';
import AdminImportLogs from './pages/AdminImportLogs';
import AdminItineraryReconciliation from './pages/AdminItineraryReconciliation';
import Friends from './pages/Friends';
import FriendCruises from './pages/FriendCruises';

// Components
import Layout from './components/layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminRoute from './components/auth/AdminRoute';
import InitialRouteHandler from './components/InitialRouteHandler';

// Context
import { AuthProvider } from './context/AuthContext';
import { CruiseProvider } from './context/CruiseContext';
import { CruiseMetadataProvider } from './context/CruiseMetadataContext';
import { AppSettingsProvider } from './context/AppSettingsContext';
import { ItineraryProvider } from './context/ItineraryContext';
import { FriendProvider } from './context/FriendContext';

// Utils
import { initializeConsentSystem } from './utils/consentInit';

// Config
import { SHOW_FRIENDS_BUTTON } from './config';

// Detect if running inside a native Capacitor app
const isNative = Capacitor.getPlatform() !== 'web';

// Pick router based on environment
const RouterWrapper: React.FC<React.PropsWithChildren> = ({ children }) =>
  isNative ? <HashRouter>{children}</HashRouter> : <BrowserRouter basename="">{children}</BrowserRouter>;

function App() {
  useEffect(() => {
    // Initialize analytics
    initializeAnalytics();

    // Initialize consent system
    initializeConsentSystem();
    
    // Hide splash screen when app is ready
    // On Android: we'll hide it when skeleton mounts instead
    // On iOS/web: hide immediately as before
    if (isNative && Capacitor.getPlatform() !== 'android') {
      SplashScreen.hide();
    }
  }, []);

  return (
    <AuthProvider>
      <AppSettingsProvider>
        <CruiseMetadataProvider>
          <ItineraryProvider>
            <CruiseProvider>
              <FriendProvider>
                <RouterWrapper>
              <AnalyticsTracker />
              <Toaster
                position="top-right"
                containerStyle={{
                  // 64px navbar + any display cutout inset + a small breathing room
                  // Use CSS variable which includes SystemBars fallback for WebView v144 fix
                  top: 'calc(var(--nav-height) + var(--safe-top) + 8px)',
                  right: '8px',
                }}
              />
              <BackButtonHandler /> {/* ✅ handle Android back button */}
              <Routes>
                <Route path="/" element={<Layout />}>
                  <Route index element={<InitialRouteHandler />} />
                  <Route path="login" element={<Login />} />
                  <Route path="signup" element={<Signup />} />
                  <Route path="verify-email" element={<VerifyEmail />} />
                  <Route path="reset-password" element={<ResetPassword />} />
                  <Route path="help" element={<Help />} />

                  <Route
                    path="dashboard"
                    element={
                      <ProtectedRoute>
                        <Dashboard />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="highlights"
                    element={
                      <ProtectedRoute>
                        <Highlights />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="rewind/:year?"
                    element={
                      <ProtectedRoute>
                        <Rewind />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="collection"
                    element={
                      <ProtectedRoute>
                        <Collection />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ship/:cruiseLine/:shipName"
                    element={
                      <ProtectedRoute>
                        <ShipDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="ship-class/:shipClass"
                    element={
                      <ProtectedRoute>
                        <ShipClass />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="add-cruise"
                    element={
                      <ProtectedRoute>
                        <AddCruise />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="edit-cruise/:id"
                    element={
                      <ProtectedRoute>
                        <EditCruise />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="post-cruise-details/:id"
                    element={
                      <ProtectedRoute>
                        <PostCruiseDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="cruise/:id"
                    element={
                      <ProtectedRoute>
                        <CruiseDetails />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="profile"
                    element={
                      <ProtectedRoute>
                        <UserProfile />
                      </ProtectedRoute>
                    }
                  />
                  {SHOW_FRIENDS_BUTTON && (
                    <>
                      <Route
                        path="friends"
                        element={
                          <ProtectedRoute>
                            <Friends />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="friend/:friendId/cruises"
                        element={
                          <ProtectedRoute>
                            <FriendCruises />
                          </ProtectedRoute>
                        }
                      />
                    </>
                  )}
                  <Route
                    path="port-cleanup"
                    element={
                      <ProtectedRoute>
                        <PortCleanup />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="admin"
                    element={
                      <AdminRoute>
                        <Admin />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/itinerary-upload"
                    element={
                      <AdminRoute>
                        <AdminItineraryUpload />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/ship-class-normalization"
                    element={
                      <AdminRoute>
                        <ShipClassNormalization />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/analytics"
                    element={
                      <AdminRoute>
                        <AdminAnalytics />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/integrity"
                    element={
                      <AdminRoute>
                        <AdminDataIntegrity />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/ship-itinerary-manager"
                    element={
                      <AdminRoute>
                        <AdminShipItineraryManager />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/photo-manager"
                    element={
                      <AdminRoute>
                        <AdminPhotoManager />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/imports"
                    element={
                      <AdminRoute>
                        <AdminImportLogs />
                      </AdminRoute>
                    }
                  />
                  <Route
                    path="admin/reconciliation"
                    element={
                      <AdminRoute>
                        <AdminItineraryReconciliation />
                      </AdminRoute>
                    }
                  />
                  <Route path="*" element={<NotFound />} />
                </Route>
              </Routes>
            </RouterWrapper>
              </FriendProvider>
          </CruiseProvider>
        </ItineraryProvider>
      </CruiseMetadataProvider>
    </AppSettingsProvider>
  </AuthProvider>
  );
}

export default App;